/**
 * Die Datums-Fassade.
 *
 * Das ist die EINZIGE Datei im Projekt, die `date-fns` importieren darf
 * (durchgesetzt per ESLint-Regel `no-restricted-imports`). Dadurch ist ein
 * spaeterer Wechsel auf `Temporal` ein Ein-Verzeichnis-Umbau statt einer
 * projektweiten Suche.
 *
 * Kernidee: Wir rechnen mit "floating wall clock"-Strings. Ein `Date`-Objekt
 * ist hier immer nur ein Zwischenschritt und verlaesst diese Datei nur dort,
 * wo der Aufrufer es ausdruecklich braucht.
 */
import {
  addDays as fnsAddDays,
  addMinutes as fnsAddMinutes,
  addMonths as fnsAddMonths,
  differenceInCalendarDays as fnsDiffDays,
  format as fnsFormat,
  getISOWeek as fnsGetISOWeek,
  startOfMonth as fnsStartOfMonth,
  endOfMonth as fnsEndOfMonth,
  startOfWeek as fnsStartOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';

import type { LocalDate, LocalDateTime } from '@/domain/types';

/** Deutschland: Woche beginnt am Montag. */
const WEEK_OPTS = { weekStartsOn: 1 as const, locale: de };

export const MINUTES_PER_DAY = 1440;

// ---------------------------------------------------------------------------
// Parsen und Formatieren von floating-Strings
// ---------------------------------------------------------------------------

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function isLocalDate(value: string): value is LocalDate {
  return DATE_RE.test(value);
}

export function isLocalDateTime(value: string): value is LocalDateTime {
  return DATETIME_RE.test(value);
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/**
 * Wandelt einen floating-String in ein `Date` in der LOKALEN Zeitzone um.
 * Fuer Anzeige und Kalenderarithmetik - nicht fuer die Serienexpansion
 * (die rechnet bewusst in UTC-Feldern, siehe `lib/recurrence/expand.ts`).
 */
export function parseLocal(value: LocalDate | LocalDateTime): Date {
  const dt = DATETIME_RE.exec(value);
  if (dt) {
    return new Date(+dt[1]!, +dt[2]! - 1, +dt[3]!, +dt[4]!, +dt[5]!, 0, 0);
  }
  const d = DATE_RE.exec(value);
  if (d) {
    return new Date(+d[1]!, +d[2]! - 1, +d[3]!, 0, 0, 0, 0);
  }
  throw new Error(`Kein gueltiger lokaler Zeitwert: ${value}`);
}

/** `Date` -> `'YYYY-MM-DD'` (lokale Felder, kein UTC). */
export function toLocalDate(date: Date): LocalDate {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `Date` -> `'YYYY-MM-DDTHH:mm'` (lokale Felder, kein UTC). */
export function toLocalDateTime(date: Date): LocalDateTime {
  return `${toLocalDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Der Datumsanteil eines beliebigen floating-Wertes. */
export function datePart(value: LocalDate | LocalDateTime): LocalDate {
  return value.slice(0, 10);
}

/** Minuten seit Mitternacht. Fuer ganztaegige Werte immer 0. */
export function minutesOfDay(value: LocalDate | LocalDateTime): number {
  if (value.length < 16) return 0;
  return +value.slice(11, 13)! * 60 + +value.slice(14, 16)!;
}

/** Baut `'YYYY-MM-DDTHH:mm'` aus Datum und Minuten seit Mitternacht. */
export function atMinutes(date: LocalDate, minutes: number): LocalDateTime {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.round(minutes)));
  return `${date}T${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
}

// ---------------------------------------------------------------------------
// Arithmetik auf floating-Strings
// ---------------------------------------------------------------------------

export function addMinutes(value: LocalDateTime, minutes: number): LocalDateTime {
  return toLocalDateTime(fnsAddMinutes(parseLocal(value), minutes));
}

export function addDays<T extends LocalDate | LocalDateTime>(value: T, days: number): T {
  const next = fnsAddDays(parseLocal(value), days);
  return (value.length > 10 ? toLocalDateTime(next) : toLocalDate(next)) as T;
}

export function addWeeks<T extends LocalDate | LocalDateTime>(value: T, weeks: number): T {
  return addDays(value, weeks * 7);
}

export function addMonths(value: LocalDate, months: number): LocalDate {
  return toLocalDate(fnsAddMonths(parseLocal(value), months));
}

/** Ganze Kalendertage zwischen zwei Werten (b - a). */
export function differenceInDays(a: LocalDate | LocalDateTime, b: LocalDate | LocalDateTime): number {
  return fnsDiffDays(parseLocal(b), parseLocal(a));
}

/** Differenz in Minuten (b - a). Fuer floating-Werte also ohne DST-Sprung. */
export function differenceInMinutes(a: LocalDateTime, b: LocalDateTime): number {
  return differenceInDays(datePart(a), datePart(b)) * MINUTES_PER_DAY
    + (minutesOfDay(b) - minutesOfDay(a));
}

export function startOfWeek(value: LocalDate | LocalDateTime): LocalDate {
  return toLocalDate(fnsStartOfWeek(parseLocal(value), WEEK_OPTS));
}

/** Letzter Tag der Woche (Sonntag), inklusiv. */
export function endOfWeek(value: LocalDate | LocalDateTime): LocalDate {
  return addDays(startOfWeek(value), 6);
}

export function startOfMonth(value: LocalDate | LocalDateTime): LocalDate {
  return toLocalDate(fnsStartOfMonth(parseLocal(value)));
}

export function endOfMonth(value: LocalDate | LocalDateTime): LocalDate {
  return toLocalDate(fnsEndOfMonth(parseLocal(value)));
}

/** Wochentag als Zahl: 0 = Sonntag, 1 = Montag ... 6 = Samstag. */
export function weekday(value: LocalDate | LocalDateTime): number {
  return parseLocal(value).getDay();
}

/** Kalenderwoche nach ISO 8601. */
export function isoWeek(value: LocalDate | LocalDateTime): number {
  return fnsGetISOWeek(parseLocal(value));
}

/** `'2026-W38'` - der Schluessel des Weekly Review. */
export function isoWeekKey(value: LocalDate | LocalDateTime): string {
  const d = parseLocal(value);
  return `${fnsFormat(d, 'RRRR', WEEK_OPTS)}-W${pad(fnsGetISOWeek(d))}`;
}

// ---------------------------------------------------------------------------
// Jetzt
// ---------------------------------------------------------------------------

export function today(): LocalDate {
  return toLocalDate(new Date());
}

export function now(): LocalDateTime {
  return toLocalDateTime(new Date());
}

/** Echter Zeitstempel mit Offset - nur fuer `createdAt` / `updatedAt`. */
export function nowInstant(): string {
  return new Date().toISOString();
}

// ---------------------------------------------------------------------------
// Deutsche Ausgabe
// ---------------------------------------------------------------------------

/** Formatiert mit `date-fns`-Muster und deutschem Locale. */
export function formatDe(value: LocalDate | LocalDateTime, pattern: string): string {
  return fnsFormat(parseLocal(value), pattern, WEEK_OPTS);
}

/** `'16:30'` */
export const formatTime = (v: LocalDateTime) => formatDe(v, 'HH:mm');
/** `'16.09.2026'` */
export const formatDate = (v: LocalDate | LocalDateTime) => formatDe(v, 'dd.MM.yyyy');
/** `'Mi, 16. September 2026'` */
export const formatDateLong = (v: LocalDate | LocalDateTime) => formatDe(v, 'EEEEEE, d. MMMM yyyy');
/** `'Mi'` */
export const formatWeekdayShort = (v: LocalDate | LocalDateTime) => formatDe(v, 'EEEEEE');
/** `'September 2026'` */
export const formatMonthYear = (v: LocalDate | LocalDateTime) => formatDe(v, 'MMMM yyyy');

/**
 * Ganztaegige Zeitraeume werden EXKLUSIV gespeichert, aber INKLUSIV angezeigt.
 * Das ist die eine Stelle, an der umgerechnet wird.
 */
export function allDayEndToInclusive(endExclusive: LocalDate): LocalDate {
  return addDays(endExclusive, -1);
}

export function allDayEndToExclusive(endInclusive: LocalDate): LocalDate {
  return addDays(endInclusive, 1);
}

/**
 * Echte Laenge eines Kalendertags in Minuten in der lokalen Zeitzone - am
 * Tag der Zeitumstellung 1380 bzw. 1500 statt 1440 (F-28).
 */
export function tagesMinuten(tag: LocalDate): number {
  return Math.round((parseLocal(addDays(tag, 1)).getTime() - parseLocal(tag).getTime()) / 60_000);
}
